import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, LangToggle } from "@/context/LanguageContext";

const LINKS = [
  { labelKey: "nav.founders", href: "#founders", testid: "nav-link-founders" },
  { labelKey: "nav.pathways", href: "#pathways", testid: "nav-link-pathways" },
  { labelKey: "nav.packages", href: "#packages", testid: "nav-link-packages" },
  { labelKey: "nav.stories", href: "#stories", testid: "nav-link-stories" },
];

export const scrollTo = (selector) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(selector, { offset: -72 });
  } else {
    document.querySelector(selector)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useLanguage();

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(href);
  };

  const accountLink = user ? (
    <Link
      to={user.role === "admin" ? "/admin" : "/dashboard"}
      data-testid="nav-account-link"
      className="text-sm text-white/60 hover:text-volt transition-colors duration-300 uppercase tracking-widest font-medium"
    >
      {user.role === "admin" ? t("nav.admin") : t("nav.dashboard")}
    </Link>
  ) : (
    <Link
      to="/login"
      data-testid="nav-login-link"
      className="text-sm text-white/60 hover:text-volt transition-colors duration-300 uppercase tracking-widest font-medium"
    >
      {t("nav.login")}
    </Link>
  );

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
      data-testid="main-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => go(e, "#top")}
          className="font-cabinet font-black text-lg tracking-tight uppercase"
          data-testid="nav-logo"
        >
          Two Shores<span className="text-volt">.</span>
          <span className="hidden sm:inline text-white/40 font-medium normal-case tracking-normal text-sm ml-2">
            Collegiate
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={l.testid}
              className="text-sm text-white/60 hover:text-volt transition-colors duration-300 uppercase tracking-widest font-medium"
            >
              {t(l.labelKey)}
            </a>
          ))}
          <LangToggle />
          {accountLink}
          <button
            onClick={(e) => go(e, "#contact")}
            data-testid="nav-cta"
            className="rounded-full bg-volt text-obsidian font-cabinet font-bold text-sm uppercase tracking-wide px-6 py-2.5 hover:bg-white transition-colors duration-300"
          >
            {t("nav.cta")}
          </button>
        </nav>

        <div className="md:hidden flex items-center gap-5">
          <LangToggle />
          <button
            className="text-white"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-black/90 border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  className="font-cabinet text-2xl font-bold uppercase text-white/80"
                >
                  {t(l.labelKey)}
                </a>
              ))}
              {accountLink}
              <button
                onClick={(e) => go(e, "#contact")}
                className="rounded-full bg-volt text-obsidian font-cabinet font-bold uppercase px-6 py-3 w-fit"
              >
                {t("nav.cta")}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
